import {useMemo} from 'react';
import {useProviderStoresMap} from '../../stores/hooks/useProviderStoresMap';
import {useStoresMap} from '../../stores/hooks/useStoresMap';
import {useMarketersMap} from '../../marketers/hooks/useMarketersMap';
import {useAuth} from '../../auth/hooks/useAuth';
import {useStoreSelectionStore} from '../../stores/store/StoreSelectionStore';
import {useMarketerSelectionStore} from '../../marketers/store/MarketerSelectionStore';
import {AuthorizationService} from '../../../Services/authorization/AuthorizationService';
import {useAuthorization} from '../../auth/hooks/useAuthorization';
import {PERMISSIONS} from '../../../constants/permissions';
import {MapViewMode} from '../types/viewMode.types';

/**
 * Interface for dashboard state return type
 */
interface DashboardState {
  // User information
  user: any;
  isProvider: boolean;
  brandNames: string[];

  // Store data
  selectedStore: any;
  selectedStoreCount: number;
  currentStoresError: any;

  // Marketer data
  selectedMarketer: any;
  selectedMarketerCount: number;
  marketersError: any;
}

/**
 * Hook for managing dashboard state with conditional data loading based on permissions and view mode
 * @param viewMode - Current view mode to determine which data to load
 * @returns DashboardState object containing user info, stores, marketers data and errors
 */
export const useDashboardState = (viewMode: MapViewMode = 'stores'): DashboardState => {
  // Authentication and authorization
  const {user} = useAuth();
  const {hasPermission} = useAuthorization();
  const isProvider = AuthorizationService.getInstance().isServiceProvider(user);
  // Handle both User class instance and plain object from localStorage
  const brandNames = user?.getAssignedBrandNames
    ? user.getAssignedBrandNames()
    : (user as any)?.providerData?.assignedBrands?.map((b: any) => b.name) || [];

  // Permission-based data loading control
  const hasStorePermissions = hasPermission(PERMISSIONS.MANAGE_STORES);
  const hasMarketerPermissions = hasPermission(PERMISSIONS.MANAGE_MARKETERS);

  // Load data based on view mode - support 'both' to show stores and marketers together
  const shouldLoadStores = hasStorePermissions && (viewMode === 'stores' || viewMode === 'both');
  const shouldLoadMarketers = hasMarketerPermissions && (viewMode === 'marketers' || viewMode === 'both');

  // Conditional data fetching based on permissions and view mode
  const {error: providerStoresError, stores: providerStores} = useProviderStoresMap(shouldLoadStores);
  const {error: storesError, stores: regularStores} = useStoresMap(shouldLoadStores);
  const {error: marketersError} = useMarketersMap(shouldLoadMarketers);

  // Store selection state
  const {selectedStore} = useStoreSelectionStore();
  const {selectedMarketer} = useMarketerSelectionStore();

  // Computed values based on user type
  const currentStores = useMemo(() => {
    return isProvider ? providerStores : regularStores;
  }, [isProvider, providerStores, regularStores]);

  const currentStoresError = useMemo(() => {
    return isProvider ? providerStoresError : storesError;
  }, [isProvider, providerStoresError, storesError]);

  const selectedStoreCount = useMemo(() => {
    return selectedStore ? 1 : 0;
  }, [selectedStore]);

  const selectedMarketerCount = useMemo(() => {
    return selectedMarketer ? 1 : 0;
  }, [selectedMarketer]);

  return {
    // User info
    user,
    isProvider,
    brandNames,

    // Data
    currentStores,

    // Selection
    selectedStore,
    selectedMarketer,
    selectedStoreCount,
    selectedMarketerCount,

    // Errors
    currentStoresError,
    marketersError
  };
};
